/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { TFunction } from 'i18next';
import type { User, UserUpdateRequestBody } from '../managers/sso';
import { DateUtils } from './dateUtils';
import { validateDialingCode, validateMobileNumber } from './signupLoginUtils';

export type SignUpFormFieldValues = {
  selectedGender: string;
  givenName: string;
  surName: string;
  dob: string;
  dialingCode: string;
  mobileNumber: string;
  requireParentalConsent: boolean;
  hasParentalConsent: boolean;
};

export type SignUpFormFieldErrors = {
  genderError: string;
  givenNameError: string;
  surNameError: string;
  dobError: string;
  dialingCodeError: string;
  mobileError: string;
};

export type SignUpProfileFields = {
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
};

/**
 * True when every mandatory sign-up form field is present on the user.
 * Form submit already requires all fields; this is the same gate for routing
 * (e.g. email OTP → Home vs sign-up form).
 */
export function isProfileComplete(user?: User | null): boolean {
  const phone = user?.phoneNumber?.trim() || user?.mobilePhone?.trim() || '';

  return !!(
    user?.firstName?.trim() &&
    user?.lastName?.trim() &&
    user?.gender?.trim() &&
    user?.dateOfBirth?.trim() &&
    user?.dialingCode?.trim() &&
    phone
  );
}

/** Build the shared profile payload used by incomplete-signup updateUser. */
export function buildSignUpProfileUpdateBody(
  fields: SignUpProfileFields,
): UserUpdateRequestBody {
  return {
    firstName: fields.firstName,
    lastName: fields.lastName,
    gender: fields.gender,
    dateOfBirth: fields.dateOfBirth,
  };
}

/**
 * Normalize signup DOB to API shape `DDMMYYYY`.
 * Returns `''` when the input is empty or cannot be parsed as a real date.
 */
export function formatSignUpDateOfBirth(dob: string): string {
  const trimmed = dob.trim();
  if (!trimmed) return '';

  const parsed = DateUtils.parseFlexibleDate(trimmed);
  if (!parsed) return '';

  return DateUtils.formatToDDMMYYYY(parsed);
}

/**
 * Validate signup form fields. Returns errors + whether the form is valid.
 * Does not mutate React state — callers apply the returned errors.
 */
export function validateSignUpFormFields(
  values: SignUpFormFieldValues,
  t: TFunction,
): { isValid: boolean; errors: SignUpFormFieldErrors } {
  const requiredFieldError = t('signUpFormView.fieldRequired');
  const formattedDob = formatSignUpDateOfBirth(values.dob);

  const errors: SignUpFormFieldErrors = {
    genderError: values.selectedGender
      ? ''
      : t('signUpFormView.genderRequired'),
    givenNameError: values.givenName ? '' : requiredFieldError,
    surNameError: values.surName ? '' : requiredFieldError,
    dobError: formattedDob ? '' : requiredFieldError,
    dialingCodeError: validateDialingCode(values.dialingCode, t),
    mobileError: validateMobileNumber(values.mobileNumber, t),
  };

  const hasFieldErrors = Object.values(errors).some(Boolean);
  const parentalBlocked =
    values.requireParentalConsent && !values.hasParentalConsent;

  return {
    isValid: !hasFieldErrors && !parentalBlocked,
    errors,
  };
}
